import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { HardDrive, Plus, Ban } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { EdgeCredentialSummary } from '@aeris/contracts'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  Input,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { LoadFailed } from '../components/load-failed.tsx'
import {
  ApiError,
  createEdgeCredential,
  fetchBranches,
  fetchEdgeCredentials,
  fetchTeam,
  revokeEdgeCredential,
} from '../lib/api.ts'

/**
 * As máquinas da loja (F041).
 *
 * ## Que problema esta tela resolve
 *
 * Até aqui, um Aeris Edge sincronizava com a nuvem usando o **cookie de uma
 * pessoa**, colado num arquivo de configuração. Aquilo expirava em trinta dias,
 * morria quando essa pessoa trocava a senha, e carregava todas as permissões
 * dela — o PC do balcão com acesso ao financeiro do gerente que o instalou.
 *
 * Aqui a credencial pertence à máquina, vale para uma filial, faz uma coisa só
 * e é revogável nesta tela.
 *
 * ## Por que "Máquinas da loja" e não "Credenciais"
 *
 * Quem abre isto está pensando no computador do balcão que parou de mandar as
 * vendas, não num conceito de autenticação. A coluna que responde a essa
 * pergunta é **Último contato**, e é ela que substitui a validade: a credencial
 * não expira — revogar já vale na requisição seguinte —, então o sinal de que
 * algo está errado não é uma data no futuro, é um silêncio no passado.
 *
 * ## O token aparece uma vez
 *
 * O banco guarda o hash. Nem o servidor consegue mostrá-lo de novo, e a tela
 * diz isso enquanto o token está na frente da pessoa — descobrir depois, com o
 * Edge já instalado numa loja, custa uma viagem até lá.
 */

const DIA = 24 * 60 * 60 * 1_000

/**
 * "há 6 dias", e não uma data.
 *
 * A pergunta é "faz muito tempo?", e um `13/08/2026 03:41` obriga quem lê a
 * fazer a subtração de cabeça — que é exatamente o passo que ninguém dá quando
 * está com pressa.
 */
const desdeQuando = (iso: string | null): string => {
  if (iso === null) return 'nunca'

  const decorrido = Date.now() - new Date(iso).getTime()
  if (decorrido < 60 * 60 * 1_000) return 'há menos de uma hora'
  if (decorrido < DIA)
    return `há ${String(Math.floor(decorrido / (60 * 60 * 1_000)))} h`

  const dias = Math.floor(decorrido / DIA)
  return dias === 1 ? 'ontem' : `há ${String(dias)} dias`
}

/**
 * Quando o silêncio deixa de ser normal.
 *
 * Um Edge fala com a nuvem a cada trinta segundos quando há internet. Um dia
 * inteiro sem contato não é oscilação de link: é a loja fechada, a máquina
 * desligada ou o Aeris parado nela.
 */
const calado = (item: EdgeCredentialSummary): boolean =>
  item.revokedAt === null &&
  (item.lastSeenAt === null ||
    Date.now() - new Date(item.lastSeenAt).getTime() > DIA)

export const EdgePage = (): ReactNode => {
  const queryClient = useQueryClient()

  const [formOpen, setFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [branchId, setBranchId] = useState('')
  const [actsAsUserId, setActsAsUserId] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  /*
   * Revogar tira do ar o computador do balcão na requisição seguinte: pergunta
   * antes, e guarda o motivo (F132). Era um clique solto na linha.
   */
  const [revogando, setRevogando] = useState<EdgeCredentialSummary | null>(null)
  const [emitido, setEmitido] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['edge', 'credentials'],
    queryFn: ({ signal }) => fetchEdgeCredentials(signal),
  })

  const branches = useQuery({
    queryKey: ['organization', 'branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
  })

  const team = useQuery({
    queryKey: ['team', 'members', 'para-credencial'],
    queryFn: ({ signal }) => fetchTeam({ status: 'active' }, signal),
  })

  const items = query.data?.items ?? []

  const emitir = useMutation({
    mutationFn: async () =>
      await createEdgeCredential({ branchId, name, actsAsUserId }),
    onSuccess: async (response) => {
      setErro(null)
      setFormOpen(false)
      setEmitido(response.token)
      await queryClient.invalidateQueries({ queryKey: ['edge', 'credentials'] })
    },
    onError: (cause: unknown) => {
      setErro(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível emitir a credencial.'
      )
    },
  })

  const revogar = useMutation({
    mutationFn: async (input: { id: string; reason: string | null }) =>
      await revokeEdgeCredential(input.id, input.reason),
    onSuccess: async () => {
      setErro(null)
      setRevogando(null)
      await queryClient.invalidateQueries({ queryKey: ['edge', 'credentials'] })
    },
    onError: (cause: unknown) => {
      setErro(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível revogar a credencial.'
      )
    },
  })

  const abrirFormulario = (): void => {
    setName('')
    setBranchId(branches.data?.branches[0]?.id ?? '')
    setActsAsUserId(team.data?.items[0]?.userId ?? '')
    setErro(null)
    setFormOpen(true)
  }

  return (
    <>
      <ConfirmDialog
        open={revogando !== null}
        onOpenChange={(open) => {
          if (!open) setRevogando(null)
        }}
        title={`Revogar ${revogando?.name ?? 'esta máquina'}?`}
        description="Ela para de sincronizar e de vender offline na próxima conexão. Para voltar, é preciso emitir uma credencial nova e instalar de novo."
        confirmLabel="Revogar"
        cancelLabel="Voltar"
        tone="danger"
        loading={revogar.isPending}
        reason={{ label: 'Motivo', placeholder: 'Ex.: computador trocado' }}
        onConfirm={(reason) => {
          if (revogando === null) return
          revogar.mutate({
            id: revogando.id,
            reason:
              (reason ?? '').trim().length === 0 ? null : (reason ?? '').trim(),
          })
        }}
      />

      <TabHeader
        title="Máquinas da loja"
        description="Os computadores que rodam o Aeris na loja e continuam vendendo sem internet. Cada um tem a sua credencial, e ela vale para uma filial só."
        actions={
          <Button onClick={abrirFormulario}>
            <Plus aria-hidden="true" className="h-4 w-4" />
            Nova máquina
          </Button>
        }
      />

      {erro === null ? null : (
        <p className="text-(length:--text-sm) text-(--danger)">{erro}</p>
      )}

      {query.isPending ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : query.isError ? (
        <LoadFailed
          what="as máquinas da loja"
          onRetry={() => {
            void query.refetch()
          }}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<HardDrive aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma máquina cadastrada"
          description="Quando você instalar o Aeris Edge num computador da loja, emita aqui a credencial dele. É ela que deixa aquele PC mandar as vendas para a nuvem sem depender de ninguém estar logado."
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Máquina</TableHeaderCell>
                <TableHeaderCell>Filial</TableHeaderCell>
                <TableHeaderCell>Responde por</TableHeaderCell>
                <TableHeaderCell>Último contato</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.name}
                    {item.revokedAt === null ? null : (
                      <Badge tone="neutral">
                        {item.revokedReason ?? 'revogada'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{item.branchName}</TableCell>
                  <TableCell>{item.actsAsDisplayName}</TableCell>
                  <TableCell>
                    {/*
                      O aviso é aqui e não numa contagem no topo: quem olha
                      esta tela está procurando QUAL máquina parou, e um
                      número sem nome manda a pessoa procurar de novo.
                    */}
                    {calado(item) ? (
                      <Badge tone="warning">
                        {desdeQuando(item.lastSeenAt)}
                      </Badge>
                    ) : (
                      desdeQuando(item.lastSeenAt)
                    )}
                  </TableCell>
                  <TableCell>
                    {item.revokedAt === null ? (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setRevogando(item)
                        }}
                      >
                        <Ban aria-hidden="true" className="h-4 w-4" />
                        Revogar
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {formOpen ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setFormOpen(false)
          }}
        >
          <DialogContent
            title="Nova máquina"
            description="Dê um nome que você reconheça daqui a seis meses, quando precisar revogar sem estar na loja."
          >
            <div className="flex flex-col gap-3">
              <Field
                label="Nome"
                hint="Como você vai reconhecer esta máquina daqui a seis meses"
              >
                {(field) => (
                  <Input
                    {...field}
                    value={name}
                    maxLength={120}
                    autoFocus
                    placeholder="Balcão — PC da frente"
                    onChange={(event) => {
                      setName(event.target.value)
                    }}
                  />
                )}
              </Field>

              <Field label="Filial" hint="A credencial vale só para esta">
                {(field) => (
                  <Select
                    {...field}
                    value={branchId}
                    onChange={(event) => {
                      setBranchId(event.target.value)
                    }}
                  >
                    {(branches.data?.branches ?? []).map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {/*
                Quem responde pelo que a máquina grava.

                Um caixa aberto tem um responsável e a coluna é obrigatória —
                uma máquina não é uma pessoa. Escolhido aqui em vez de deduzido
                de quem clicou: quem instala o Edge costuma ser o dono, e o
                turno pertence a quem fica no balcão.
              */}
              <Field
                label="Responde por"
                hint="Quem assina o caixa que esta máquina abrir"
              >
                {(field) => (
                  <Select
                    {...field}
                    value={actsAsUserId}
                    onChange={(event) => {
                      setActsAsUserId(event.target.value)
                    }}
                  >
                    {(team.data?.items ?? []).map((member) => (
                      <option key={member.userId} value={member.userId}>
                        {member.displayName}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => {
                  setFormOpen(false)
                }}
              >
                Cancelar
              </Button>
              <Button
                disabled={
                  emitir.isPending ||
                  name.trim() === '' ||
                  branchId === '' ||
                  actsAsUserId === ''
                }
                onClick={() => {
                  emitir.mutate()
                }}
              >
                Emitir credencial
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      {emitido === null ? null : (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setEmitido(null)
          }}
        >
          <DialogContent
            title="Copie agora"
            description="Este código aparece uma vez só. Ele não fica guardado em lugar nenhum que possa mostrá-lo de novo — se você perder, emita outro e revogue este."
          >
            {/*
              Selecionável e quebrando linha, num tipo monoespaçado: este
              texto vai ser copiado para dentro de um arquivo de configuração,
              e um caractere trocado por semelhança visual é uma hora de
              suporte.
            */}
            <code className="block rounded-(--radius-md) bg-(--surface-sunken) p-3 font-mono text-(length:--text-sm) break-all select-all">
              {emitido}
            </code>

            <p className="text-(length:--text-sm) text-(--text-secondary)">
              No computador da loja, este código vai na linha{' '}
              <code>EDGE_CLOUD_TOKEN</code> do arquivo de configuração do Aeris
              Edge.
            </p>

            <DialogFooter>
              <Button
                onClick={() => {
                  setEmitido(null)
                }}
              >
                Já copiei
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
